import React, { useState } from 'react';
import { Alert, Container, Button, Form } from 'react-bootstrap';
import Header from '../../layout/header/Header';
import Footer from '../../layout/footer/Footer';
import axios from '../../api/axios';
import './Contact.css';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        comment: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!formData.name.trim() || !formData.email.trim() || !formData.comment.trim()) {
            setError('Please fill in name, email, and message.');
            setSuccess('');
            return;
        }

        setIsSubmitting(true);
        setError('');
        setSuccess('');

        try {
            await axios.post('/contact-messages', {
                name: formData.name.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                message: formData.comment.trim(),
            });

            setSuccess('Your message has been sent successfully. We will contact you soon.');
            setFormData({ name: '', email: '', phone: '', comment: '' });
        } catch (submitError: any) {
            setError(submitError?.response?.data?.message || 'Unable to send message right now. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Header />
            <section className="arkab-contact-hero">
                <Container>
                    <h1>Contact Us</h1>
                    <p>Reach our team for inquiries, support requests, and product guidance.</p>
                </Container>
            </section>

            <Container className="arkab-contact-grid my-5">
                <div className="arkab-contact-card arkab-contact-info">
                    <h2>Get in Touch</h2>
                    <p>For any inquiries, feedback, or assistance, send us a message and our team will reach out with the right support.</p>
                    <div className="arkab-contact-highlights">
                        <article className="arkab-contact-highlight">
                            <h3>Fast Response</h3>
                            <p>Most messages are reviewed within one business day.</p>
                        </article>
                        <article className="arkab-contact-highlight">
                            <h3>Private and Safe</h3>
                            <p>Your details stay internal and are only used to reply to your request.</p>
                        </article>
                        <article className="arkab-contact-highlight arkab-contact-highlight-wide">
                            <h3>Need Product Guidance?</h3>
                            <p>Share your preferred style, budget, or category and we will suggest suitable options.</p>
                        </article>
                    </div>
                </div>

                <div className="arkab-contact-card arkab-contact-form-wrap">
                    <h2>Send a Message</h2>
                    {error ? <Alert variant="danger">{error}</Alert> : null}
                    {success ? <Alert variant="success">{success}</Alert> : null}

                    <Form onSubmit={handleSubmit} className="arkab-contact-form">
                        <Form.Group className="mb-3" controlId="ContactForm-name">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="contactName"
                                placeholder="Your full name"
                                value={formData.name}
                                onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="ContactForm-email">
                            <Form.Label>Email <span aria-hidden="true">*</span></Form.Label>
                            <Form.Control
                                type="email"
                                name="contactEmail"
                                placeholder="you@example.com"
                                value={formData.email}
                                onChange={(event) => setFormData((prev) => ({ ...prev, email: event.target.value }))}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="ContactForm-phone">
                            <Form.Label>Phone number</Form.Label>
                            <Form.Control
                                type="tel"
                                name="contactPhone"
                                placeholder="03XX-XXXXXXX"
                                pattern="[0-9\-+ ]*"
                                value={formData.phone}
                                onChange={(event) => setFormData((prev) => ({ ...prev, phone: event.target.value }))}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="ContactForm-body">
                            <Form.Label>Message</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={7}
                                name="contactMessage"
                                placeholder="Tell us how we can help..."
                                value={formData.comment}
                                onChange={(event) => setFormData((prev) => ({ ...prev, comment: event.target.value }))}
                                required
                            />
                        </Form.Group>
                        <Button variant="dark" type="submit" className="arkab-send-btn" disabled={isSubmitting}>
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                        </Button>
                    </Form>
                </div>
            </Container>
            <Footer />
        </>
    );
}

export default Contact;